/**
 * Regelversion 8: Die soziale Schere (Gini-Index 0–100, Start 29,5).
 *
 * Was sie bewegt:
 *  - Gesetze: Wer Arbeitnehmer, Familien, Rentner und Junge stärkt und Vermögende belastet, verkleinert sie.
 *    Wer Unternehmen und Spitzenverdiener entlastet oder Sozialleistungen kürzt, vergrößert sie.
 *    Wirkt verteilt über die Monate nach dem Inkrafttreten (wie die Kosten eines Gesetzes).
 *  - Arbeitslosigkeit und Inflation vergrößern sie. Ohne Gegensteuern wächst sie langsam (Vermögen wachsen schneller als Löhne).
 * Was sie bewirkt:
 *  - Über 30,5: Arbeitnehmer und Familien werden unzufriedener, die Sozialverbände protestieren,
 *    die Spaltung im Land wächst, es gibt häufiger Warnstreiks und Großstreiks.
 *  - Unter 28: Arbeitnehmer und Familien sind zufriedener.
 * Alles hier greift nur, wenn `since(8)` gilt.
 */
import { OUTLETS } from '../content/parties';
import { on } from '../content/settings';
import { rnd } from './rng';
import { S, log } from './state';
import { clamp, de } from './util';
import { since } from './version';
import { press7 } from './news7';
import { woGini } from './order11';

export const GINI0 = 29.5, GINI_HI = 30.5, GINI_LO = 28, GINI_DRIFT = .012;
export const fairOn = (): boolean => since(8);
export const giniNow = (): number => S.gini ?? GINI0;
/** Anzeige als Gini-Index (0–100). */
export const giniText = (g = giniNow()): string => de(g, 1);

/** Wirkung eines Entwurfs auf die Schere (Gini-Punkte). Aufruf aus lawFx. */
export function applyGini(fx: any): any {
  if (!fairOn() || fx?.gini != null) return fx; // Steuerreform und Standortpaket bringen ihren Wert mit
  const g = fx?.g || {}, pos = fx?.pos || {};
  // Relativ: Wer profitiert im Verhältnis? Vorteile für Unternehmen zählen nur teilweise (Investitionen nützen auch Beschäftigten).
  const low = (g.arb || 0) * .35 + (g.fam || 0) * .3 + (g.rent || 0) * .2 + (g.jung || 0) * .15;
  let d = -(low - (g.unt || 0) * .4) * .08 - (fx?.rich || 0) * .03 - (pos.sozial || 0) * .08;
  if ((pos.buergergeld || 0) > 0) d += pos.buergergeld * .15; // Grundsicherung kürzen
  d = clamp(Math.round(d * 100) / 100, -1.5, 1.5);
  return Math.abs(d) >= .02 ? { ...fx, gini: d } : fx;
}

/** Monatlich. */
export function fairTick(): boolean {
  if (!fairOn() || S.phase !== 'govern') return false;
  S.gini ??= GINI0;
  // Ohne Gegensteuern wächst die Schere langsam: Vermögen wachsen schneller als Löhne.
  const target = GINI0 + GINI_DRIFT * S.m + (S.giniLaw || 0) + (S.al - 6.2) * .35 + (on('infl') ? (S.infl - 2) * .15 : 0) + ((S.flightU || 0) + (S.flightA || 0)) * .05 + woGini(); // Version 11: Wirtschaftsordnung
  S.gini = Math.round((S.gini + (clamp(target, 22, 40) - S.gini) * .08) * 1000) / 1000;
  if (S.m % 2 === 0) { const h = (S.giniHist ??= []); h.push(S.gini); if (h.length > 120) h.shift(); }
  const g = S.gini;
  if (g > GINI_HI) {
    const x = g - GINI_HI;
    S.bonus.arb -= x * .05; S.bonus.fam -= x * .04;
    if (S.lob) S.lob.soz = clamp((S.lob.soz ?? 50) - x * .15, 0, 100);
    if ((S as any).polar != null) (S as any).polar = clamp((S as any).polar + x * .04, 0, 100);
    if (rnd() < x * .025) {
      const w = ['Bahn', 'Kitas', 'Krankenhäuser', 'Flughäfen', 'Nahverkehr', 'Häfen'][Math.floor(rnd() * 6)];
      S.shockGr -= .03; log(`Warnstreiks: ${w} stehen für einen Tag still. Die Gewerkschaften verweisen auf die wachsende Kluft zwischen Arm und Reich.`, 'bad');
      press7(OUTLETS[2].n, `Warnstreik: ${w} stehen still`, -.4);
      return true;
    }
  } else if (g < GINI_LO) { const x = GINI_LO - g; S.bonus.arb += x * .03; S.bonus.fam += x * .02; }
  return false;
}

/** Zusätzliche Chance auf einen Großstreik pro Monat. */
export const strikeChance = (): number => (fairOn() ? clamp((giniNow() - 31) * .008, 0, .05) : 0);
